"use client";

import { useReducer } from "react";
import { lazyComponent } from "@/components/ui/lazy-component";
import { LoadFailureNotice } from "@/components/ui/load-failure-notice";
import Link from "next/link";
import { ArrowLeft } from "@/components/icons";
import type { RfqType } from "@convex/constants/types";
import {
  LoadingStep,
  PublicListingWizard as PublicWizardInner,
  PublicRfqWizard as RfqWizard,
} from "@/components/request-quote/public-quote-wizards";
import { ServiceTypeStep } from "@/components/rfq/wizard/steps/service-type-step";
import { WizardProgress } from "@/components/rfq/wizard/wizard-progress";
import {
  QuoteFlowBreadcrumbs,
  type QuoteFlowCrumb,
} from "@/components/request-quote/quote-flow-breadcrumbs";
import { getProductTypeBySlug } from "@/lib/generated/product-types";
import { RFQ_TYPE_LABELS } from "@/lib/rfq/constants";
import {
  quoteEntryFromSearchParams,
  type QuoteEntryProps,
} from "@/components/rfq/wizard/request-quote-prefill";
import { useWindowSearchParams } from "@/hooks/use-window-search-params";
import { cn } from "@/lib/utils";
import { RepackifyLogo } from "@/components/brand/repackify-logo";
import { Button } from "@/components/ui/button";

const ProductTypeStep = lazyComponent(
  () =>
    import("@/components/rfq/wizard/steps/product-type-step").then(
      (module) => module.ProductTypeStep,
    ),
  {
    loading: <LoadingStep />,
    failure: {
      label: "quote-product-type-step",
      fallback: <LoadFailureNotice title="The quote form could not load." />,
    },
  },
);

export function RequestQuoteRouter() {
  const entry = quoteEntryFromSearchParams(useWindowSearchParams());
  const entryKey = [
    entry.initialType,
    entry.initialProductSlug,
    entry.initialQuantity,
    JSON.stringify(entry.initialLocation ?? null),
    JSON.stringify(entry.initialFormValues ?? null),
  ].join("|");
  return <QuoteFlow key={entryKey} {...entry} />;
}

interface QuoteSelection {
  selectedType: RfqType | null;
  selectedProduct: { slug: string; name: string } | null;
  direction: "forward" | "backward" | null;
}

type SelectionAction =
  | { type: "service"; service: RfqType }
  | { type: "product"; slug: string; name: string }
  | { type: "back" }
  | { type: "restart" }
  | { type: "choose-product" };

function selectionReducer(state: QuoteSelection, action: SelectionAction): QuoteSelection {
  switch (action.type) {
    case "service":
      return { ...state, direction: "forward", selectedType: action.service,
        selectedProduct: action.service === "buy" ? null : state.selectedProduct };
    case "product":
      return { ...state, direction: "forward", selectedProduct: { slug: action.slug, name: action.name } };
    case "back":
      return state.selectedProduct
        ? { ...state, direction: "backward", selectedProduct: null }
        : { ...state, direction: "backward", selectedType: null };
    case "restart":
      return { direction: "backward", selectedType: null, selectedProduct: null };
    case "choose-product":
      return { ...state, direction: "backward", selectedProduct: null };
  }
}

function useQuoteSelection({ initialType, initialProductSlug }: QuoteEntryProps) {
  const [selection, dispatch] = useReducer(selectionReducer, {
    selectedType: initialType ?? null,
    selectedProduct: initialProductSlug
      ? { slug: initialProductSlug, name: getProductTypeBySlug(initialProductSlug)?.displayName ?? "" }
      : null,
    direction: null,
  });
  const goToService = () => {
    dispatch({ type: "restart" });
    const url = new URL(window.location.href);
    if (url.searchParams.has("step")) {
      url.searchParams.delete("step");
      window.history.replaceState(
        window.history.state,
        "",
        url.pathname + url.search,
      );
    }
  };
  return {
    ...selection,
    goToService,
    goToProduct: () => dispatch({ type: "choose-product" }),
    handleBack: () => dispatch({ type: "back" }),
    handleServiceSelect: (service: RfqType) => dispatch({ type: "service", service }),
    handleProductSelect: (slug: string, name: string) => dispatch({ type: "product", slug, name }),
  };
}

type QuoteSelectionFlow = ReturnType<typeof useQuoteSelection>;

function QuoteFlow(props: QuoteEntryProps) {
  const flow = useQuoteSelection(props);
  if (flow.selectedType === "buy") {
    return (
      <RfqWizard
        mode="anonymous"
        defaultRfqType="buy"
        defaultProductSlug={props.initialProductSlug}
        defaultQuantity={props.initialQuantity}
        defaultLocation={props.initialLocation}
        initialFormValues={props.initialFormValues}
        prefilledFields={props.prefilledFields}
        skipServiceStep={true}
        onExitToService={flow.goToService}
      />
    );
  }
  if (flow.selectedProduct && (flow.selectedType === "sell" || flow.selectedType === "recycle")) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-background via-background to-muted/20">
        <PublicWizardInner
          intent={flow.selectedType}
          productSlug={flow.selectedProduct.slug}
          productName={flow.selectedProduct.name}
          initialQuantity={props.initialQuantity}
          initialLocation={props.initialLocation}
          onBackToService={flow.goToService}
          onBackToProduct={flow.goToProduct}
        />
      </div>
    );
  }
  return <QuoteSelectionScreen flow={flow} />;
}

function QuoteSelectionScreen({ flow }: { flow: QuoteSelectionFlow }) {
  const isServiceStep = !flow.selectedType;
  const breadcrumbs: QuoteFlowCrumb[] = flow.selectedType
    ? [
        { key: "type", label: RFQ_TYPE_LABELS[flow.selectedType], onClick: flow.goToService },
        { key: "product", label: "Packaging" },
      ]
    : [];
  const animationClass = flow.direction === null ? undefined : flow.direction === "forward"
    ? "animate-in fade-in slide-in-from-right-4 duration-300"
    : "animate-in fade-in slide-in-from-left-4 duration-300";
  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-background via-background to-muted/20 flex flex-col">
      <WizardProgress currentIndex={isServiceStep ? 0 : 1} totalSteps={5} />
      <QuoteRouterHeader canGoBack={!isServiceStep} onBack={flow.handleBack} />
      <main className="flex-1 flex flex-col px-6 sm:px-10 lg:px-16 py-8">
        <div className="w-full max-w-3xl mx-auto">
          <QuoteFlowBreadcrumbs crumbs={breadcrumbs} className="mb-6" />
          <div key={isServiceStep ? "service" : "product"} className={cn(animationClass)}>
            <QuoteSelectionStep flow={flow} />
          </div>
        </div>
      </main>
    </div>
  );
}

function QuoteSelectionStep({ flow }: { flow: QuoteSelectionFlow }) {
  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">
          {!flow.selectedType ? "Choose your starting point"
            : flow.selectedType === "sell" ? "What packaging are you selling?"
              : "What packaging are you recycling?"}
        </h1>
      </div>
      {!flow.selectedType ? (
        <ServiceTypeStep value="buy" onSelect={flow.handleServiceSelect} />
      ) : (
        <ProductTypeStep value={flow.selectedProduct?.slug ?? ""} onSelect={flow.handleProductSelect} />
      )}
    </>
  );
}

function QuoteRouterHeader({
  canGoBack,
  onBack,
}: {
  canGoBack: boolean;
  onBack: () => void;
}) {
  return (
    <header className="flex items-center justify-between px-6 sm:px-10 pt-6 pb-2">
      <div className="w-24">
        {canGoBack && (
          <Button
            variant="quiet"
            size="sm"
            type="button"
            onClick={onBack}
            className="-ml-3"
            aria-label="Go back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">Back</span>
          </Button>
        )}
      </div>
      <Link
        href="/"
        prefetch={false}
        className="hover:opacity-80 transition-opacity"
      >
        <RepackifyLogo className="h-8" />
      </Link>
    </header>
  );
}
